import { useEffect, useRef, useState } from 'react';
import { trpc } from '../lib/trpc';

interface DailyMessagePreviewModalProps {
  isOpen: boolean;
  botId: string | null;
  dailyMessageTemplate: string;
  communicationSchedule?: PreviewInput['communicationSchedule'];
  onClose: () => void;
}

type PreviewResult = NonNullable<
  ReturnType<typeof trpc.bots.previewDailyMessage.useMutation>['data']
>;
type PreviewInput = Parameters<
  ReturnType<typeof trpc.bots.previewDailyMessage.useMutation>['mutate']
>[0];
type PreviewUser = NonNullable<
  ReturnType<typeof trpc.bots.searchUsers.useQuery>['data']
>[number];

export function DailyMessagePreviewModal({
  isOpen,
  botId,
  dailyMessageTemplate,
  communicationSchedule,
  onClose,
}: DailyMessagePreviewModalProps) {
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState<PreviewUser | null>(null);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingPreviewRequestId, setPendingPreviewRequestId] = useState<number | null>(null);
  const latestPreviewRequestId = useRef(0);
  const previousBotId = useRef<string | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const titleId = useRef(`daily-message-preview-title-${Math.random().toString(36).slice(2)}`);

  const rawTrimmedQuery = searchInput.trim();
  const trimmedQuery = debouncedSearchInput.trim();
  const isDebouncingSearch = rawTrimmedQuery !== trimmedQuery;
  const hasSearchQuery = trimmedQuery.length >= 2;
  const searchUsersQuery = trpc.bots.searchUsers.useQuery(
    {
      botId: botId ?? '',
      query: trimmedQuery,
      limit: 8,
    },
    {
      enabled: isOpen && !!botId && hasSearchQuery,
    }
  );

  const previewMutation = trpc.bots.previewDailyMessage.useMutation();
  const previewResetRef = useRef(previewMutation.reset);

  const users = searchUsersQuery.data ?? [];
  const searchErrorMessage = searchUsersQuery.error?.message || null;
  const isCurrentPreviewPending =
    previewMutation.isPending && pendingPreviewRequestId === latestPreviewRequestId.current;

  useEffect(() => {
    previewResetRef.current = previewMutation.reset;
  }, [previewMutation.reset]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const syncViewport = () => {
      setIsNarrowViewport(window.innerWidth < 960);
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);

    return () => window.removeEventListener('resize', syncViewport);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    searchInputRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) {
        return;
      }

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      const focusable = Array.from(focusableElements).filter((element) => element.offsetParent !== null);

      if (focusable.length === 0) {
        return;
      }

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setDebouncedSearchInput('');
      return;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [isOpen, searchInput]);

  useEffect(() => {
    if (!isOpen) {
      previouslyFocusedElementRef.current?.focus();
      previouslyFocusedElementRef.current = null;
      setSearchInput('');
      setDebouncedSearchInput('');
      setSelectedUser(null);
      setResult(null);
      setErrorMessage(null);
      setPendingPreviewRequestId(null);
      previousBotId.current = null;
      previewResetRef.current();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (previousBotId.current !== botId) {
      setSearchInput('');
      setDebouncedSearchInput('');
      setSelectedUser(null);
      previousBotId.current = botId;
    }

    latestPreviewRequestId.current += 1;
    setResult(null);
    setErrorMessage(null);
    setPendingPreviewRequestId(null);
    previewResetRef.current();
  }, [botId, dailyMessageTemplate, isOpen]);

  const handleRunPreview = () => {
    if (!botId || !selectedUser) {
      return;
    }

    const requestId = latestPreviewRequestId.current + 1;
    latestPreviewRequestId.current = requestId;
    setResult(null);
    setErrorMessage(null);
    setPendingPreviewRequestId(requestId);

    previewMutation.mutate({
      botId,
      userId: selectedUser.id,
      dailyMessageTemplate,
      communicationSchedule,
    }, {
      onSuccess: (data) => {
        setPendingPreviewRequestId((currentRequestId) => (
          currentRequestId === requestId ? null : currentRequestId
        ));

        if (requestId !== latestPreviewRequestId.current) {
          return;
        }

        setErrorMessage(null);
        setResult(data);
      },
      onError: (error) => {
        setPendingPreviewRequestId((currentRequestId) => (
          currentRequestId === requestId ? null : currentRequestId
        ));

        if (requestId !== latestPreviewRequestId.current) {
          return;
        }

        setResult(null);
        setErrorMessage(error.message || 'Failed to generate preview');
      },
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        style={styles.modal}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={styles.header}>
          <div>
            <h3 id={titleId.current} style={styles.title}>Daily Check-In Preview</h3>
            <p style={styles.subtitle}>
              Search a user from this bot, preview with the current unsaved prompt, and inspect the raw prompt payload.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close daily check-in preview" style={styles.closeButton}>
            ×
          </button>
        </div>

        <div
          style={{
            ...styles.body,
            ...(isNarrowViewport ? styles.bodyNarrow : {}),
          }}
        >
          <div style={styles.controlsColumn}>
            <label style={styles.label}>
              Search users
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  setSelectedUser(null);
                  latestPreviewRequestId.current += 1;
                  setResult(null);
                  setErrorMessage(null);
                  setPendingPreviewRequestId(null);
                }}
                placeholder="Name, user ID prefix, or platform ID prefix"
                style={styles.input}
              />
            </label>

            <div style={styles.searchPanel}>
              {rawTrimmedQuery.length === 0 ? (
                <div style={styles.searchEmpty}>Enter a search term to find users in this bot.</div>
              ) : rawTrimmedQuery.length < 2 ? (
                <div style={styles.searchEmpty}>Enter at least 2 characters to search this bot&apos;s users.</div>
              ) : isDebouncingSearch || searchUsersQuery.isLoading ? (
                <div style={styles.searchEmpty}>Searching users...</div>
              ) : searchErrorMessage ? (
                <div style={styles.searchError}>Failed to search users: {searchErrorMessage}</div>
              ) : users.length === 0 ? (
                <div style={styles.searchEmpty}>No matching users found for this bot.</div>
              ) : (
                users.map((user) => {
                  const isSelected = user.id === selectedUser?.id;
                  return (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        latestPreviewRequestId.current += 1;
                        setResult(null);
                        setErrorMessage(null);
                        setPendingPreviewRequestId(null);
                      }}
                      style={{
                        ...styles.userOption,
                        ...(isSelected ? styles.userOptionSelected : {}),
                      }}
                    >
                      <div style={styles.userPrimaryRow}>
                        <span style={styles.userName}>{user.name || 'Unnamed user'}</span>
                        <span style={styles.userPlatform}>{user.platform}</span>
                      </div>
                      <div style={styles.userMeta}>{user.id}</div>
                      <div style={styles.userMeta}>Platform ID: {user.platformUserId}</div>
                    </button>
                  );
                })
              )}
            </div>

            <div style={styles.selectionBox}>
              <div style={styles.selectionLabel}>Selected user</div>
              <div style={styles.selectionValue}>
                {selectedUser
                  ? `${selectedUser.name || 'Unnamed user'} (${selectedUser.id})`
                  : 'No user selected'}
              </div>
            </div>

            <button
              onClick={handleRunPreview}
              disabled={!botId || !selectedUser || isCurrentPreviewPending}
              style={{
                ...styles.previewButton,
                ...((!botId || !selectedUser || isCurrentPreviewPending) ? styles.previewButtonDisabled : {}),
              }}
            >
              {isCurrentPreviewPending ? 'Generating Preview...' : 'Run Preview'}
            </button>

            {errorMessage && (
              <div style={styles.errorBox}>{errorMessage}</div>
            )}
          </div>

          <div style={styles.resultsColumn}>
            {result ? (
              <>
                <div
                  style={{
                    ...styles.metricsRow,
                    ...(isNarrowViewport ? styles.metricsRowNarrow : {}),
                  }}
                >
                  <div style={styles.metricCard}>
                    <span style={styles.metricLabel}>Model</span>
                    <span style={styles.metricValue}>{result.model}</span>
                  </div>
                  <div style={styles.metricCard}>
                    <span style={styles.metricLabel}>Tokens</span>
                    <span style={styles.metricValue}>{result.usage.totalTokens}</span>
                  </div>
                  <div style={styles.metricCard}>
                    <span style={styles.metricLabel}>Latency</span>
                    <span style={styles.metricValue}>{result.latencyMs} ms</span>
                  </div>
                </div>

                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Generated Message</div>
                  {result.warnings.length > 0 ? (
                    <div style={styles.warningBox}>
                      {result.warnings.join(' ')}
                    </div>
                  ) : null}
                  <textarea readOnly value={result.content} style={styles.readOnlyTextareaLarge} />
                </div>

                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Raw Prompt Payload</div>
                  <textarea
                    readOnly
                    value={JSON.stringify(result.rawPrompt.fullPrompt, null, 2)}
                    style={styles.readOnlyTextarea}
                  />
                </div>

                <div style={styles.section}>
                  <div style={styles.sectionTitle}>User Prompt</div>
                  <textarea readOnly value={result.rawPrompt.userPrompt} style={styles.readOnlyTextarea} />
                </div>

                <div style={styles.section}>
                  <div style={styles.sectionTitle}>System Prompt</div>
                  <textarea readOnly value={result.rawPrompt.systemPrompt} style={styles.readOnlyTextarea} />
                </div>
              </>
            ) : (
              <div style={styles.resultPlaceholder}>
                Select a user and run a preview to inspect the generated check-in and raw prompt.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(35, 31, 26, 0.56)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(12px, 3vw, 24px)',
    zIndex: 1000,
  },
  modal: {
    width: 'min(1200px, 100%)',
    maxHeight: '90vh',
    overflow: 'hidden',
    background: '#FAF7F1',
    borderRadius: '20px',
    boxShadow: '0 24px 60px rgba(48, 39, 30, 0.24)',
    border: '1px solid rgba(103, 91, 74, 0.16)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '24px 24px 16px',
    borderBottom: '1px solid rgba(103, 91, 74, 0.12)',
  },
  title: {
    margin: 0,
    fontSize: '22px',
    color: '#30271E',
  },
  subtitle: {
    margin: '8px 0 0',
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#6B6258',
  },
  closeButton: {
    border: 'none',
    background: 'transparent',
    color: '#6B6258',
    fontSize: '24px',
    cursor: 'pointer',
    lineHeight: 1,
  },
  body: {
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    gap: '20px',
    padding: '24px',
    overflow: 'auto',
  },
  bodyNarrow: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    padding: '16px',
  },
  controlsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#4C443B',
  },
  input: {
    borderRadius: '10px',
    border: '1px solid #D7CDBF',
    background: '#FFFDFC',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#30271E',
  },
  searchPanel: {
    border: '1px solid #E0D7C9',
    borderRadius: '14px',
    background: '#FFFDFC',
    minHeight: '220px',
    maxHeight: '320px',
    overflowY: 'auto',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  searchEmpty: {
    padding: '16px',
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#7D7469',
  },
  searchError: {
    padding: '16px',
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#A33B32',
  },
  warningBox: {
    marginBottom: '10px',
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid #E2B65C',
    background: '#FFF6E2',
    color: '#745115',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  userOption: {
    textAlign: 'left',
    borderRadius: '12px',
    border: '1px solid #E9E1D5',
    background: '#FFFDFC',
    padding: '12px',
    cursor: 'pointer',
  },
  userOptionSelected: {
    border: '1px solid #5A8A5E',
    background: '#EDF6EE',
  },
  userPrimaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '4px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#30271E',
  },
  userPlatform: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#5A8A5E',
    textTransform: 'uppercase',
  },
  userMeta: {
    fontSize: '12px',
    color: '#6B6258',
    lineHeight: 1.4,
    wordBreak: 'break-all',
  },
  selectionBox: {
    borderRadius: '12px',
    background: '#F3EBDD',
    padding: '12px',
  },
  selectionLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#7D7469',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '6px',
  },
  selectionValue: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#30271E',
    wordBreak: 'break-word',
  },
  previewButton: {
    border: 'none',
    borderRadius: '12px',
    padding: '12px 16px',
    background: '#2F6B5F',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  previewButtonDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  errorBox: {
    borderRadius: '12px',
    background: '#FDEDEC',
    color: '#A33B32',
    padding: '12px',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  resultsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0,
  },
  metricsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '12px',
  },
  metricsRowNarrow: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  metricCard: {
    borderRadius: '14px',
    background: '#FFFDFC',
    border: '1px solid #E0D7C9',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  metricLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#7D7469',
  },
  metricValue: {
    fontSize: '14px',
    color: '#30271E',
    wordBreak: 'break-word',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#4C443B',
  },
  readOnlyTextareaLarge: {
    minHeight: '140px',
    resize: 'vertical',
    borderRadius: '14px',
    border: '1px solid #DCCFBE',
    background: '#FFFDFC',
    padding: '14px',
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#30271E',
  },
  readOnlyTextarea: {
    minHeight: '220px',
    resize: 'vertical',
    borderRadius: '14px',
    border: '1px solid #DCCFBE',
    background: '#FFFDFC',
    padding: '14px',
    fontSize: '12px',
    lineHeight: 1.6,
    color: '#30271E',
    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
  },
  resultPlaceholder: {
    borderRadius: '16px',
    border: '1px dashed #D7CDBF',
    background: 'rgba(255, 253, 252, 0.72)',
    color: '#7D7469',
    padding: '24px',
    fontSize: '14px',
    lineHeight: 1.6,
  },
};
